import React, { useEffect, useState, useMemo } from 'react';
import { useSprings, animated } from 'react-spring';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactComponent as SlackIcon } from '../../../assets/slack-icon.svg';
import zeplinColor from '../../../theme/Color';

interface Card {
  id: string | number;
  account: string;
  isSlack: boolean;
  sender: string;
  channel: string;
  text?: string;
}

interface CardStackProps {
  cards: Card[];
  renderCard: (
    card: Card,
    isExpanded: boolean,
    isLast: boolean,
    isFirst: boolean,
    totalCards: number,
    handleClick: () => void,
  ) => React.ReactNode;
  height?: number;
  isTab?: boolean;
  extraHeightToshowmore?: number;
  isLoginBtn?: boolean;
}

const CardStack: React.FC<CardStackProps> = ({
  cards,
  renderCard,
  height = 150, // Increased base height for first card
  isTab = false,
  extraHeightToshowmore = 28,
  isLoginBtn = false,
}) => {
  const theme = useTheme();
  const [isStackExpanded, setIsStackExpanded] = useState<boolean>(false);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  // Responsive breakpoints for text length
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl')); // 1200px - 1536px
  // xl and above: > 1536px

  // Get responsive text length threshold
  const getTextLengthThreshold = () => {
    if (isXs) return 20; // Very small screens
    if (isSm) return 25; // Small screens
    if (isMd) return 28; // Medium screens
    if (isLg) return 35; // Large screens
    return 58; // Extra large screens
  };

  // Get responsive long text threshold for additional height
  const getLongTextThreshold = () => {
    if (isXs) return 80; // Mobile
    if (isSm) return 90; // Small tablet
    if (isMd) return 100; // Large tablet
    if (isLg) return 110; // Desktop
    return 170; // Large desktop
  };

  // Calculate individual card heights
  const cardHeights = useMemo(() => {
    const textLengthThreshold = getTextLengthThreshold();
    const longTextThreshold = getLongTextThreshold();

    return cards.map((card, index) => {
      if (isLoginBtn) return isTab ? (isLg || isMd || isXs || isSm ? 115 : 128) : 128;

      if (index === 0) {
        // First card: channel info + text + sender + time + notifications count
        console.log(card.account.length, 'card.account.length', card?.account);
        const hasLongText = card.account && card.account.length > textLengthThreshold;
        const baseFirstCardHeight = hasLongText ? 99 : 79;
        // const hasLongText = card.account && card.account.length > 66;
        const hasChannel = card.channel && card.channel.length > 0;
        const hasMultiCards = cards.length > 1;

        return isTab
          ? 80 +
              (hasChannel ? 35 : 0) +
              (hasMultiCards && !isStackExpanded ? 33 : 0) +
              (card.account && card.account.length > longTextThreshold ? 20 : 0)
          : baseFirstCardHeight +
              (hasChannel ? 35 : 0) +
              (hasMultiCards && !isStackExpanded ? 25 : 0);
        // (hasMultiCards ? extraHeightToshowmore : 0)
      } else {
        // Subsequent cards: sender + text + time only
        const hasLongText = card.account && card.account.length > textLengthThreshold;
        const baseSubsequentHeight = hasLongText ? 99 : 79;

        return isTab
          ? 80 + (card.account && card.account.length > longTextThreshold ? 20 : 0)
          : baseSubsequentHeight;
      }
    });
  }, [cards, isLoginBtn, isStackExpanded]);

  // Calculate Y positions for each card
  const yPositions = useMemo(() => {
    if (!isStackExpanded) {
      // Collapsed: stack with small offsets
      return cards.map((_, index) => index * 10);
    }

    // Expanded: position each card below the previous with proper spacing
    let currentY = 0;
    const positions = [];

    for (let i = 0; i < cards.length; i++) {
      positions.push(currentY);
      currentY += cardHeights[i] + 12; // Add gap between cards
    }

    return positions;
  }, [cards, cardHeights, isStackExpanded]);
  console.log(yPositions, 'yPositions', cardHeights, 'cardHeights');
  // Calculate total container height
  useEffect(() => {
    if (cards.length === 0) return;

    if (isStackExpanded) {
      // Expanded: sum all card heights plus gaps
      const totalCardHeight = cardHeights.reduce((sum, height) => sum + height, 0);
      const gaps = (cards.length - 1) * 12; // Gaps between cards
      const showLessButtonHeight = 30; // Space for "Show Less" button

      setContainerHeight(totalCardHeight + gaps + showLessButtonHeight);
    } else {
      // Collapsed: use first card height plus stack offsets
      const stackOffset = Math.min(cards.length - 1, 3) * 10; // Max 3 visible stacked cards
      const firstCardHeight = cardHeights[0] || height;

      setContainerHeight(firstCardHeight + stackOffset);
    }
  }, [cards.length, cardHeights, isStackExpanded, height]);

  const springs = useSprings(
    cards.length,
    cards.map((card, i) => {
      const yPosition = yPositions[i];

      // Width decreases for stacked cards when collapsed
      const widthPercentage = isStackExpanded ? 100 : 100 - i * 3;

      return {
        y: yPosition,
        width: `${widthPercentage}%`,
        height: isStackExpanded ? cardHeights[i] : cardHeights[0], // Use actual heights when expanded
        opacity: isStackExpanded || i < 3 ? 1 : 0, // Hide cards beyond 3rd when collapsed
        config: { tension: 170, friction: 26 },
      };
    }),
  );

  const handleCardClick = () => {
    setIsStackExpanded(!isStackExpanded);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: containerHeight + (isTab ? (isStackExpanded ? 20 : 20) : 0),
        display: 'flex',
        gap: { xs: '10px', xl: '16px' },
        transition: 'height 0.3s ease-out',
      }}
    >
      {isTab && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: cards.length + 2,
          }}
        >
          <Box
            sx={{
              borderRadius: '100px',
              border: `1px solid ${zeplinColor.Neutral4}`,
              padding: '20px',
              width: '26px',
              height: '26px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: zeplinColor.Neutral1,
            }}
          >
            <Box
              sx={{
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
                width: { xs: '25px', xl: '30px' },
                height: { xs: '25px', xl: '30px' },
              }}
            >
              <SlackIcon width={18} height={18} />
            </Box>
          </Box>
          <Box
            sx={{
              width: '2px',
              flexGrow: 1,
              backgroundColor: zeplinColor.Neutral3,
            }}
          />
        </Box>
      )}

      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {springs.map((props, i) => (
          <animated.div
            key={cards[i].id}
            style={{
              position: 'absolute',
              width: props.width,
              height: isStackExpanded ? props.height : cardHeights[0],
              left: '50%',
              borderRadius: '14px',
              backgroundColor: isTab ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
              boxShadow:
                cards.length < 2 || isStackExpanded ? 'none' : '0px 3px 8px rgba(0, 0, 0, 0.1)',
              cursor: i === 0 && cards.length > 1 ? 'pointer' : 'default',
              transform: `translateX(-50%)`,
              opacity: props.opacity,
              top: props.y,
              zIndex: isStackExpanded ? cards.length - i : i === 0 ? 99 : cards.length - i,
              overflow: isStackExpanded ? 'visible' : 'hidden',
              transition: 'all 0.3s ease-out',
              maxHeight: isStackExpanded ? 'none' : cardHeights[0],
            }}
            onClick={i === 0 && cards.length > 1 ? handleCardClick : undefined}
          >
            {(isStackExpanded || i < 3) &&
              renderCard(
                cards[i],
                isStackExpanded,
                i === cards.length - 1,
                i === 0,
                cards.length,
                handleCardClick,
              )}
          </animated.div>
        ))}

        {/* Show Less button when expanded */}
        {isStackExpanded && cards.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: yPositions[cards.length - 1] + cardHeights[cardHeights.length - 1] + 8,
              left: '16px',
              zIndex: cards.length + 1,
              cursor: 'pointer',
            }}
            onClick={handleCardClick}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.Brand1}>
              Show Less
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CardStack;
